import { Trash2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import ContentBlockFormBody from '@/containers/ContentBlocks/components/ContentBlockFormBody'
import { ContentBlockItem } from '@/containers/ContentBlocks/components/DraggableContentBlockItem'
import { ContentBlockFormValues } from '@/containers/ContentBlocks/types'

interface BlockEditPanelProps {
  blockId: number | 'new'
  onBack: () => void
  onCreated: (block: ContentBlockItem) => void
  onUpdated: (block: ContentBlockItem) => void
  /**
   * Open the remove-block choice dialog for this block (remove from page vs
   * delete permanently). Owned by PageDetails, which also returns to page
   * settings after a successful delete. Optional/backward-compatible.
   */
  onRequestDelete?: (blockId: number) => void
  /**
   * This block's previously staged settings-form snapshot (if any), owned by
   * PageDetails. Only relevant when editing an existing block (`blockId` is a
   * number) — a 'new' block still creates immediately via the form's own
   * Create button, so it never has a draft. See `ContentBlockFormBody`'s
   * `draft` prop doc.
   */
  draft?: ContentBlockFormValues | null
  /**
   * Stage every settings-form change into PageDetails' per-block draft
   * instead of mutating immediately. Only wired for existing blocks — see
   * `draft` doc above. `seededNameByLanguage` is the per-language name the
   * settings form was seeded with — see `ContentBlockFormBody`'s
   * `onDraftChange` doc and `PageDetails.handleStageFormValues`.
   */
  onStageFormValues?: (
    blockId: number,
    values: ContentBlockFormValues,
    seededNameByLanguage: Record<string, string>
  ) => void
  /**
   * This block's staged inline-title overrides (`blockDrafts[id].nameByLanguage`),
   * owned by PageDetails. Passed through so the settings form's seed matches
   * what a page Save would actually send — see `ContentBlockFormBody`'s
   * `nameOverrides` prop doc.
   */
  stagedNameByLanguage?: Record<string, string>
}

/**
 * Right-column contextual editor. Swaps in over the page-settings form while a
 * content block is selected for editing. The block form keeps its own
 * `<form>`/Save (per-block mutation); returning to settings is via the Close
 * (✕) button (`onBack`), and Delete (trash) routes through `onRequestDelete`.
 */
export default function BlockEditPanel({
  blockId,
  onBack,
  onCreated,
  onUpdated,
  onRequestDelete,
  draft,
  onStageFormValues,
  stagedNameByLanguage,
}: BlockEditPanelProps) {
  const isNew = blockId === 'new'

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button
          type="button"
          variant="ghost"
          onClick={onBack}
          title="Back to page settings"
          aria-label="Back to page settings"
          className="px-2 text-muted-foreground"
        >
          <X className="mr-2 h-4 w-4" />
          Close
        </Button>

        {/* Delete only applies to a persisted block. A 'new' block isn't in
            `contentBlocks` yet, so there's nothing to remove. */}
        {!isNew && onRequestDelete && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => onRequestDelete(blockId)}
            title="Delete block"
            aria-label="Delete block"
            className="px-2 text-destructive hover:text-destructive"
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{isNew ? 'New block' : 'Edit block'}</CardTitle>
        </CardHeader>
        <CardContent>
          <ContentBlockFormBody
            key={blockId}
            blockId={isNew ? null : blockId}
            onCreated={onCreated}
            onUpdated={onUpdated}
            onCancel={onBack}
            // Page editor: description is edited inline in the full-size preview,
            // so the settings form must not render or send it.
            hideDescription
            // Grow to natural height with no internal scrollbar — the page
            // itself provides the vertical scroll, unlike the Dialog consumers
            // which are viewport-bounded and need their own scroll region.
            bodyScrollClassName=""
            // STAGED mode — existing blocks only. A 'new' block has no id to
            // key a draft on, so it keeps the form's normal immediate Create.
            {...(!isNew && onStageFormValues
              ? {
                  draft,
                  onDraftChange: (
                    values: ContentBlockFormValues,
                    seededNameByLanguage: Record<string, string>
                  ) =>
                    onStageFormValues(
                      blockId as number,
                      values,
                      seededNameByLanguage
                    ),
                  nameOverrides: stagedNameByLanguage,
                }
              : {})}
          />
        </CardContent>
      </Card>
    </div>
  )
}
